import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const StoreCtx = createContext(null);

const loadCart = () => {
  try { return JSON.parse(localStorage.getItem("kv_cart") || "[]"); } catch { return []; }
};
const saveCart = (c) => localStorage.setItem("kv_cart", JSON.stringify(c));

export function StoreProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState(loadCart());
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("kv_token");
    if (!token) { setAuthLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem("kv_token"); })
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => { saveCart(cart); }, [cart]);

  const login = useCallback(async (email, password, turnstile_token) => {
    const { data } = await api.post("/auth/login", { email, password, turnstile_token });
    localStorage.setItem("kv_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name, turnstile_token) => {
    const { data } = await api.post("/auth/register", { email, password, name, turnstile_token });
    localStorage.setItem("kv_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("kv_token");
    setUser(null);
  }, []);

  const addToCart = useCallback((product, variant, qty = 1) => {
    setCart((prev) => {
      const key = `${product.id}:${variant.sku}`;
      const existing = prev.find((l) => `${l.product_id}:${l.sku}` === key);
      if (existing) {
        return prev.map((l) => `${l.product_id}:${l.sku}` === key ? { ...l, qty: l.qty + qty } : l);
      }
      return [...prev, {
        product_id: product.id,
        slug: product.slug,
        name: product.name,
        image: product.image,
        sku: variant.sku,
        weight: variant.weight,
        price: variant.price,
        qty,
      }];
    });
    setCartOpen(true);
  }, []);

  const updateQty = useCallback((sku, qty) => {
    setCart((prev) => prev.map((l) => l.sku === sku ? { ...l, qty: Math.max(1, qty) } : l));
  }, []);

  const removeLine = useCallback((sku) => {
    setCart((prev) => prev.filter((l) => l.sku !== sku));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const subtotal = cart.reduce((s, l) => s + l.price * l.qty, 0);
  const count = cart.reduce((s, l) => s + l.qty, 0);

  return (
    <StoreCtx.Provider value={{
      user, authLoading, login, register, logout,
      cart, cartOpen, setCartOpen, addToCart, updateQty, removeLine, clearCart,
      subtotal, count,
    }}>
      {children}
    </StoreCtx.Provider>
  );
}

export const useStore = () => useContext(StoreCtx);
